import React, { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { usePlayer } from "../context/PlayerContext";
import { useChat } from "../context/ChatContext";
import { STATIONS } from "../config/stations";
import { Play, Pause, Radio, MessageCircle, Volume2, VolumeX, Tv2 } from "lucide-react";

const TV_STATION = STATIONS.find((s) => s.id === "tv");

const GfnSoulNycTv = () => {
  const {
    current,
    playing,
    play,
    toggle,
    muted,
    toggleMute,
    setTvAnchor,
    tvStatus,
    tvError,
  } = usePlayer();
  const { setDrawerOpen } = useChat();
  const slotRef = useRef(null);
  const isTvCurrent = current?.id === "tv";

  // Auto-tune the TV when landing on this page if nothing is playing
  useEffect(() => {
    if (!current || current.id !== "tv") {
      play("tv");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track the slot rect and dock the persistent TV video into it
  const updateAnchor = useCallback(() => {
    if (!slotRef.current) return;
    const r = slotRef.current.getBoundingClientRect();
    setTvAnchor({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [setTvAnchor]);

  useEffect(() => {
    updateAnchor();
    const ro = new ResizeObserver(updateAnchor);
    if (slotRef.current) ro.observe(slotRef.current);
    window.addEventListener("scroll", updateAnchor, { passive: true });
    window.addEventListener("resize", updateAnchor);
    const id = setInterval(updateAnchor, 500); // handles hero image reflow etc.
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", updateAnchor);
      window.removeEventListener("resize", updateAnchor);
      clearInterval(id);
      setTvAnchor(null); // release the dock so it goes back to floating
    };
  }, [updateAnchor, setTvAnchor]);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      {/* Header strip */}
      <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-crimson-live live-dot" />
            </span>
            <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
              On Air · 24 / 7
            </span>
          </div>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl leading-[0.9] text-white uppercase">
            GFN <span className="text-gold-gradient">Soul NYC</span> TV
          </h1>
          <p className="mt-3 font-body text-neutral-300 text-base md:text-lg max-w-2xl">
            The live broadcast — music, culture, community — streaming straight from
            New York, 24 hours a day. Kick back and let it ride.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="tv-page-toggle"
            onClick={() => (isTvCurrent ? toggle() : play("tv"))}
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors"
          >
            {isTvCurrent && playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span>{isTvCurrent && playing ? "Pause" : "Play Live"}</span>
          </button>
          <button
            data-testid="tv-page-mute"
            onClick={toggleMute}
            className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            <span>{muted ? "Unmute" : "Mute"}</span>
          </button>
          <button
            data-testid="tv-page-chat"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-2 border border-gold/30 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Live Chat</span>
          </button>
        </div>
      </header>

      {/* Video stage — the persistent <video> is docked into this slot via setTvAnchor */}
      <div
        ref={slotRef}
        data-testid="tv-stage"
        className="w-full aspect-video rounded-lg border border-gold/40 bg-black relative overflow-hidden"
      >
        {/* Skeleton — visible only if the docked video is initializing / error */}
        {isTvCurrent && tvStatus === "error" && (
          <div className="absolute inset-0 z-20 grid place-items-center bg-black/85 text-center px-6">
            <div>
              <div className="font-condensed uppercase tracking-widest text-crimson-live text-xs mb-1">
                Stream Error
              </div>
              <div className="text-neutral-300 text-sm">{tvError || "Cannot play the live TV feed."}</div>
              <button
                data-testid="tv-page-retry"
                onClick={() => play("tv")}
                className="mt-4 inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-4 py-2 rounded-full transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {!isTvCurrent && (
          <div className="absolute inset-0 grid place-items-center">
            <button
              data-testid="tv-page-start"
              onClick={() => play("tv")}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors"
            >
              <Play className="w-4 h-4" />
              Start The Broadcast
            </button>
          </div>
        )}
      </div>

      {/* Info strip below the video */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-jet-surface border border-gold/20 rounded-lg p-5">
          <div className="flex items-center gap-2">
            <Tv2 className="w-4 h-4 text-gold" />
            <div className="font-condensed uppercase tracking-widest text-[10px] text-gold">
              Network
            </div>
          </div>
          <div className="mt-2 font-display text-2xl text-white uppercase leading-none">
            {TV_STATION?.name}
          </div>
          <div className="mt-2 font-body text-sm text-neutral-400">
            {TV_STATION?.tagline}
          </div>
        </div>
        <div className="bg-jet-surface border border-gold/20 rounded-lg p-5">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-gold" />
            <div className="font-condensed uppercase tracking-widest text-[10px] text-gold">
              Format
            </div>
          </div>
          <div className="mt-2 font-display text-2xl text-white uppercase leading-none">
            Live · 24 / 7
          </div>
          <div className="mt-2 font-body text-sm text-neutral-400">
            {TV_STATION?.genre}
          </div>
        </div>
        <div className="bg-jet-surface border border-gold/20 rounded-lg p-5">
          <div className="font-condensed uppercase tracking-widest text-[10px] text-gold">
            Now On Air
          </div>
          <div className="mt-2 font-display text-2xl text-white uppercase leading-none">
            {isTvCurrent && playing ? "Playing Live" : "Paused"}
          </div>
          <div className="mt-2 font-body text-sm text-neutral-400">
            Hit “Play Live” or tap the video to start.
          </div>
        </div>
      </div>

      {/* On Demand cross-link */}
      <div className="mt-8 rounded-lg border border-gold/20 bg-gradient-to-r from-jet-surface/80 to-jet-surface/40 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
            Also from GFN
          </div>
          <div className="mt-1 font-display text-2xl md:text-3xl uppercase text-white">
            GFN Soul On Demand
          </div>
          <div className="font-body text-sm text-neutral-400 mt-1">
            Browse the catalog, search episodes, sort by newest, and press play.
          </div>
        </div>
        <Link
          to="/on-demand"
          data-testid="tv-page-on-demand-link"
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors"
        >
          Open Catalog
        </Link>
      </div>
    </div>
  );
};

export default GfnSoulNycTv;
